import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

export type SortDirection = "asc" | "desc" | null;

export type SortableColumn<T> = {
  key: keyof T | string;
  header: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
  align?: "left" | "center" | "right";
  className?: string;
  cellClassName?: string;
};

type SortableTableProps<T> = {
  data: T[];
  columns: SortableColumn<T>[];
  keyExtractor: (item: T) => string | number;
  defaultSortKey?: string;
  defaultSortDirection?: SortDirection;
  onSort?: (key: string, direction: SortDirection) => void;
  rowClassName?: (row: T, index: number) => string;
  noDataMessage?: string;
  tableClassName?: string;
  headerClassName?: string;
  sortLocalData?: boolean;
  footerContent?: React.ReactNode;
};

export function SortableTable<T>({
  data,
  columns,
  keyExtractor,
  defaultSortKey,
  defaultSortDirection = null,
  onSort,
  rowClassName,
  noDataMessage = "Nenhum dado encontrado",
  tableClassName = "bg-white dark:bg-zinc-800 rounded-md overflow-hidden shadow-sm",
  headerClassName = "bg-zinc-100 dark:bg-zinc-900",
  sortLocalData = true,
  footerContent,
}: SortableTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey || null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection);

  const handleSort = (key: string) => {
    let newDirection: SortDirection = "asc";
    
    if (sortKey === key) {
      if (sortDirection === "asc") {
        newDirection = "desc";
      } else if (sortDirection === "desc") {
        newDirection = null;
      } else {
        newDirection = "asc";
      }
    }
    
    setSortKey(key);
    setSortDirection(newDirection);
    
    if (onSort) {
      onSort(key, newDirection);
    }
  };

  const sortedData = useMemo(() => {
    if (!sortLocalData || !sortKey || !sortDirection) {
      return data;
    }

    return [...data].sort((a: any, b: any) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];
      
      if (aValue === bValue) return 0;
      
      const comparison = aValue < bValue ? -1 : 1;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [data, sortKey, sortDirection, sortLocalData]);

  const renderSortIcon = (column: SortableColumn<T>) => {
    if (!column.sortable) return null;
    
    if (sortKey === column.key) {
      return sortDirection === "asc" ? (
        <ArrowUp className="inline-block ml-1 h-4 w-4" />
      ) : sortDirection === "desc" ? (
        <ArrowDown className="inline-block ml-1 h-4 w-4" />
      ) : (
        <ArrowUpDown className="inline-block ml-1 h-4 w-4 opacity-50" />
      );
    }
    
    return <ArrowUpDown className="inline-block ml-1 h-4 w-4 opacity-50" />;
  };

  const getTextAlignment = (align?: string) => {
    switch (align) {
      case "center": return "text-center";
      case "right": return "text-right";
      default: return "text-left";
    }
  };

  return (
    <div className={tableClassName}>
      <Table>
        <TableHeader className={headerClassName}>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.key.toString()}
                className={`${getTextAlignment(column.align)} ${column.className || ""} ${
                  column.sortable ? "cursor-pointer select-none" : ""
                }`}
                onClick={column.sortable ? () => handleSort(column.key.toString()) : undefined}
              >
                {column.header}
                {renderSortIcon(column)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.length > 0 ? (
            sortedData.map((row, index) => (
              <TableRow
                key={keyExtractor(row)}
                className={rowClassName ? rowClassName(row, index) : ""}
              >
                {columns.map((column) => (
                  <TableCell
                    key={`${keyExtractor(row)}-${column.key.toString()}`}
                    className={`${getTextAlignment(column.align)} ${column.cellClassName || ""}`}
                  >
                    {column.render
                      ? column.render(row)
                      : (row as any)[column.key] !== undefined
                      ? (row as any)[column.key]
                      : ""}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center py-6 text-gray-500"
              >
                {noDataMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {footerContent && <div className="p-3 border-t">{footerContent}</div>}
    </div>
  );
} 